/**
 * THE scene-number rules on the client — normalisation, ordering, and the
 * option list the Breakdown's inline Scenes multi-select renders.
 *
 * ── THERE IS NO MASTER SCENE TABLE ───────────────────────────────────
 * `gag_scenes` is a join table (`gag_id`, `scene_number` TEXT, `scene_sort`),
 * so there is no configured list of scenes anywhere. The picker's options are
 * HARVESTED — the distinct scene numbers already used by gags in this
 * department, from `GET /api/gags/scene-suggestions` — plus whatever this gag
 * already carries, plus anything the user types. Product owner, 2026-08-09.
 *
 * A typed scene that is not in the harvest is simply USED. There is nothing to
 * register and no master record to create, which is what makes this cheaper
 * than the supplier picker it is modelled on.
 *
 * ── 🔑 THIS IS A MIRROR, AND IT IS A CONVENIENCE, NEVER A GATE ───────
 * `sceneOptionKey` / `sceneOptionSort` / `compareSceneNumbers` mirror
 * `sceneKey` / `sceneSortValue` / `compareSceneNumbers` in
 * `server/helpers/sceneNumber.js`, which in turn match `parseScenes` in
 * `server/routes/gags.js` — the parser that actually writes `gag_scenes`. The
 * server re-parses and replaces on every save and is the authority; nothing
 * here decides anything.
 *
 * **What breaks if the mirror drifts is not security, it is the DIRTY TEST.**
 * The row's draft holds `scenes` as the comma-joined display string, and the
 * save diff compares it against the gag's own `scenes_display`. If the client
 * orders scenes differently from the server, an untouched row reports itself
 * dirty and the user is prompted to save something they never changed. That is
 * why `scenesToDisplay` is applied to BOTH sides of that comparison rather than
 * one — see `buildGagRowPatch`.
 *
 * Held in step by a case table duplicated verbatim in
 * `client/src/utils/sceneOptions.test.js` and §4 of
 * `server/tests/api/gag-scene-suggestions.test.js`: the two suites cannot both
 * be green while the rules disagree. Same device, and the same justification,
 * as `utils/poNumber.js` and `utils/supplierOptions.js`.
 *
 * 🪤 Do NOT reach for `localeCompare` or `toLocaleLowerCase` here. The server
 * folds with plain `toLowerCase()` precisely because SQLite's lowercasing is
 * ASCII-only while PostgreSQL's is locale-aware; a locale-sensitive client
 * would be a third answer to a question that already has one.
 *
 * 🪤 **`scene_number` IS TEXT AND MUST STAY TEXT.** `12A` and `45 pt 2` are
 * real. `Number('12A')` is `NaN` and `parseInt('12A')` is `12`, so a coercion
 * either drops a scene or silently merges it with a different one. Ordering is
 * what the SEPARATE `scene_sort` is for.
 */

/** Collapse whitespace runs to one space, then trim. Mirrors the server rule. */
export function normaliseSceneNumber(raw) {
  if (raw === null || raw === undefined) return '';
  return String(raw).replace(/\s+/g, ' ').trim();
}

/** The case-insensitive identity of a scene number. Mirrors the server rule. */
export function sceneOptionKey(raw) {
  return normaliseSceneNumber(raw).toLowerCase();
}

/**
 * The ordering value — the LEADING integer, or `null`.
 *
 * `parseInt`, never `Number`: `Number('12A')` is `NaN`, which would sort every
 * alphanumeric scene into the un-numbered bucket at the end so `12A` would not
 * sit beside `12`. This is byte-for-byte what `parseScenes` stores in
 * `gag_scenes.scene_sort`.
 */
export function sceneOptionSort(raw) {
  const n = parseInt(normaliseSceneNumber(raw), 10);
  return Number.isNaN(n) ? null : n;
}

/**
 * Compare two scene numbers the way the database's `ORDER BY` does:
 * `(scene_sort IS NULL), scene_sort, scene_number`.
 */
export function compareSceneNumbers(a, b) {
  const sa = sceneOptionSort(a);
  const sb = sceneOptionSort(b);
  if (sa === null && sb !== null) return 1;
  if (sa !== null && sb === null) return -1;
  if (sa !== null && sb !== null && sa !== sb) return sa - sb;
  const na = normaliseSceneNumber(a);
  const nb = normaliseSceneNumber(b);
  return na < nb ? -1 : na > nb ? 1 : 0;
}

/**
 * Split the comma-separated form `PUT /api/gags/:gagId` accepts (and that
 * `gags.scenes_display` already is) into normalised scene numbers.
 *
 * Mirrors `parseScenes`: split on commas, trim, drop empties, dedupe
 * case-insensitively keeping the FIRST spelling.
 */
export function parseSceneList(value) {
  if (value === null || value === undefined) return [];
  const seen = new Set();
  const out = [];
  for (const part of String(value).split(',')) {
    const scene = normaliseSceneNumber(part);
    if (!scene) continue;
    const key = sceneOptionKey(scene);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(scene);
  }
  return out;
}

/**
 * The canonical comma-joined form of a scene list — sorted the way the server
 * stores and returns it.
 *
 * 🔑 **This is the function that makes the dirty test honest.** Run BOTH the
 * draft and the gag's `scenes_display` through it and only a real change to the
 * SET can register; a reordering, a stray space or a duplicate cannot.
 */
export function scenesToDisplay(list) {
  const scenes = Array.isArray(list) ? parseSceneList(list.join(',')) : parseSceneList(list);
  return [...scenes].sort(compareSceneNumbers).join(', ');
}

/**
 * Merge the department's harvested scene numbers with the ones this gag already
 * has, into one de-duplicated, scene-ordered option list.
 *
 * 🔑 **The gag's own scenes must be in the list even when the harvest omits
 * them.** Two ways that happens and both are ordinary: the suggestions were
 * fetched before this gag was saved, and a scene the user has just typed into
 * the picker in this very session. An option list that could not represent the
 * current value would render a selected chip with no matching checkbox.
 *
 * @param {{scene_number: string, gag_count?: number}[]} suggestions
 *        rows from GET /api/gags/scene-suggestions
 * @param {string[]|string} selected the scenes currently on the draft
 * @returns {{scene: string, key: string, selected: boolean, gagCount: number|null}[]}
 */
export function buildSceneOptions(suggestions = [], selected = []) {
  const selectedList = Array.isArray(selected) ? selected : parseSceneList(selected);
  const selectedKeys = new Set(selectedList.map(sceneOptionKey));

  const byKey = new Map();
  const add = (raw, gagCount) => {
    const scene = normaliseSceneNumber(raw);
    if (!scene) return;
    const key = sceneOptionKey(scene);
    // First spelling wins. The harvest is added first, so the SERVER's chosen
    // spelling survives over a local one — the same "the record is the more
    // informative tier" reasoning the supplier merge uses.
    if (byKey.has(key)) return;
    byKey.set(key, { scene, key, selected: selectedKeys.has(key), gagCount });
  };

  for (const s of suggestions || []) add(s?.scene_number, s?.gag_count ?? null);
  for (const s of selectedList) add(s, null);

  return [...byKey.values()].sort((a, b) => compareSceneNumbers(a.scene, b.scene));
}

/**
 * Add a scene to a selection, or leave it untouched if it is already there
 * (case-insensitively). Returns a NEW array in canonical order.
 */
export function addScene(selected, raw) {
  const list = Array.isArray(selected) ? selected : parseSceneList(selected);
  const scene = normaliseSceneNumber(raw);
  if (!scene) return list;
  if (list.some((s) => sceneOptionKey(s) === sceneOptionKey(scene))) return list;
  return [...list, scene].sort(compareSceneNumbers);
}

/** Remove a scene from a selection, case-insensitively. Returns a NEW array. */
export function removeScene(selected, raw) {
  const list = Array.isArray(selected) ? selected : parseSceneList(selected);
  const key = sceneOptionKey(raw);
  return list.filter((s) => sceneOptionKey(s) !== key);
}
